import {
  Badge,
  Button,
  ComboBox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  IconButton,
  Input,
  Label,
  Select,
  Switch,
  TextArea,
} from '@civilgrid/ui/components';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { MergeAgenciesRequest, MergeAuditPreviewEntry } from '~/api/civilsearch';
import { civilSearchTierLabels } from '~/common/dataRequestUtils';
import { CONTACT_METHOD_LABELS } from '~/common/mergeableAgencyFields';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Agency, CivilSearchTier, ContactMethod } from '~/common/types';
import { MergeAuditPanel } from './MergeAuditPanel';
import {
  areMergeValuesEqual,
  defaultMergeValues,
  findMergeValueGroup,
  formatFieldValue,
  getCustomInputError,
  getMergeFieldError,
  getMergeFieldValue,
  groupFieldValues,
  isAgreedField,
  isEmptyMergeValue,
  MERGE_FIELD_INPUT,
  MERGEABLE_AGENCY_FIELD_LABELS,
  MERGEABLE_AGENCY_FIELD_SECTIONS,
  MERGEABLE_AGENCY_FIELDS,
  type MergeableAgencyField,
  type MergeFieldValues,
  type MergeValueGroup,
  reconcileMergeValues,
  summarizeFields,
  textToMergeValue,
  toMergeAgencyValues,
} from './mergeFields';
import { useMergeAuditPreview } from './useMergeAuditPreview';
import { ValueChip, ValueChipGroup } from './ValueChip';

export type MergeAgenciesDialogProps = {
  open: boolean;
  /** The merge inputs in selection order, 2 to 10. */
  agencies: Agency[];
  onClose: () => void;
  onSubmit: (params: MergeAgenciesRequest) => void;
  isPending: boolean;
};

/** Option lists longer than this use the searchable ComboBox instead of Select. */
const MAX_SELECT_OPTIONS = 10;

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/** The field list order: the dialog's sections, flattened. */
const FIELD_ORDER: readonly MergeableAgencyField[] = MERGEABLE_AGENCY_FIELD_SECTIONS.flatMap(({ fields }) => fields);

const CUSTOM_CHIP = 'custom';

/** Renders a merged value as text. The assignee id shows the name of an input that carries it. */
function formatMergeValue(field: MergeableAgencyField, value: unknown, agencies: Agency[]): string {
  if (value == null) return formatFieldValue(null);
  switch (field) {
    case 'defaultCivilSearchAssigneeId': {
      const assignee = agencies.find((a) => a.defaultCivilSearchAssignee?.id === value)?.defaultCivilSearchAssignee;
      return formatFieldValue(assignee?.name ?? assignee?.email ?? value);
    }
    case 'logo':
      return formatFieldValue((value as { name?: string }).name ?? null);
    case 'restrictions': {
      const r = value as { urban: number; rural: number };
      return `Urban: ${r.urban} • Rural: ${r.rural}`;
    }
    case 'preferredContactMethod':
      return CONTACT_METHOD_LABELS[value as ContactMethod] ?? String(value);
    case 'civilSearchTier':
      return civilSearchTierLabels[value as CivilSearchTier] ?? String(value);
    default:
      return formatFieldValue(value);
  }
}

const groupText = (field: MergeableAgencyField, group: MergeValueGroup, agencies: Agency[]) =>
  group.empty ? 'empty' : formatMergeValue(field, group.value, agencies);

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

function sumRepointed(preview: MergeAuditPreviewEntry): number {
  const r = preview.repointed;
  return (
    r.dataRequestsV2 +
    r.projectUtilityNotes.total +
    r.projectAgencies.total +
    r.agencyContacts.total +
    r.agencyLinks.total +
    r.agencyNotes +
    r.agencyDigitization.total +
    r.intelligenceSummaryUndigitizedAgencies.total
  );
}

/** Editable fields whose starting value is invalid open in Custom, so the control to fix them shows. */
function fieldsNeedingCustom(values: MergeFieldValues): Set<MergeableAgencyField> {
  return new Set(
    MERGEABLE_AGENCY_FIELDS.filter(
      (field) => MERGE_FIELD_INPUT[field].kind !== 'pick-only' && getMergeFieldError(field, values[field]) !== null,
    ),
  );
}

export function MergeAgenciesDialog({ open, agencies, onClose, onSubmit, isPending }: MergeAgenciesDialogProps) {
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [values, setValues] = useState<MergeFieldValues>(() => defaultMergeValues(agencies));
  const [customFields, setCustomFields] = useState<Set<MergeableAgencyField>>(() => fieldsNeedingCustom(values));
  const [showAgreed, setShowAgreed] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const showAgreedId = useId();
  const stripHeadingId = useId();
  const stripHeadingRef = useRef<HTMLHeadingElement>(null);
  const removeButtonsRef = useRef(new Map<string, HTMLButtonElement>());
  // Where focus goes once a removed agency's chip has unmounted.
  const pendingFocusRef = useRef<{ index: number } | null>(null);
  // Fields edited in their control since the last blur, so a typed override is captured once per edit.
  const typedFieldsRef = useRef(new Set<MergeableAgencyField>());
  // Fields the user picked or typed; a removal re-defaults only the others.
  const touchedFieldsRef = useRef(new Set<MergeableAgencyField>());
  const auditViewedFiredRef = useRef(false);

  const activeAgencies = useMemo(() => agencies.filter((a) => !removedIds.includes(a.id)), [agencies, removedIds]);
  const agencyIds = useMemo(() => activeAgencies.map((a) => a.id), [activeAgencies]);

  // Every open starts from the full selection and the first non-empty value of each field.
  useEffect(() => {
    if (!open || agencies.length === 0) return;
    const defaults = defaultMergeValues(agencies);
    setRemovedIds([]);
    setValues(defaults);
    setCustomFields(fieldsNeedingCustom(defaults));
    setShowAgreed(false);
    setAnnouncement('');
    typedFieldsRef.current.clear();
    touchedFieldsRef.current.clear();
    auditViewedFiredRef.current = false;
  }, [open, agencies]);

  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending) return;
    pendingFocusRef.current = null;
    const next = activeAgencies[Math.min(pending.index, activeAgencies.length - 1)];
    const button = next ? removeButtonsRef.current.get(next.id) : undefined;
    if (button && !button.disabled) button.focus();
    else stripHeadingRef.current?.focus();
  }, [activeAgencies]);

  const auditQuery = useMergeAuditPreview(open ? agencyIds : null);
  const previews = auditQuery.data?.previews;

  // Fires the first time the audit table is expanded in each open.
  const handleAuditExpanded = () => {
    if (auditViewedFiredRef.current || !previews) return;
    auditViewedFiredRef.current = true;
    capturePosthogEvent(POSTHOG_EVENTS.agencyMergeAuditViewed, {
      agency_ids: agencyIds,
      total_repointed: previews.reduce((sum, p) => sum + sumRepointed(p), 0),
      total_staying_behind: previews.reduce((sum, p) => sum + p.stayingBehind.drfRequests, 0),
    });
  };

  const groupsByField = useMemo(() => {
    const out = {} as Record<MergeableAgencyField, MergeValueGroup[]>;
    for (const field of MERGEABLE_AGENCY_FIELDS) out[field] = groupFieldValues(activeAgencies, field);
    return out;
  }, [activeAgencies]);
  const summary = useMemo(() => summarizeFields(activeAgencies), [activeAgencies]);

  const errors = useMemo(() => {
    const out: Partial<Record<MergeableAgencyField, string>> = {};
    for (const field of MERGEABLE_AGENCY_FIELDS) {
      // A typed value is also checked against its input; an agency's existing value never is.
      const error =
        getMergeFieldError(field, values[field]) ??
        (customFields.has(field) ? getCustomInputError(field, values[field]) : null);
      if (error) out[field] = error;
    }
    return out;
  }, [values, customFields]);
  const hasErrors = Object.keys(errors).length > 0;
  const hasBlockers = (previews ?? []).some((p) => (p.blockers?.overlappingSubGeometries?.length ?? 0) > 0);
  const tooFew = activeAgencies.length < 2;
  const auditLoading = !tooFew && auditQuery.isFetching;
  const visibleFields = FIELD_ORDER.flatMap((field) => {
    const groups = groupsByField[field];
    const isCustom = customFields.has(field);
    // A field in error or in Custom is never hidden.
    const expanded = !isAgreedField(groups) || isCustom || errors[field] !== undefined;
    return expanded || showAgreed ? [{ field, groups, expanded, isCustom }] : [];
  });
  const emptyValueCount = MERGEABLE_AGENCY_FIELDS.filter((field) => isEmptyMergeValue(values[field])).length;

  const setFieldValue = (field: MergeableAgencyField, value: unknown) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const setCustom = (field: MergeableAgencyField, on: boolean) =>
    setCustomFields((prev) => {
      if (prev.has(field) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(field);
      else next.delete(field);
      return next;
    });

  const captureTyped = (field: MergeableAgencyField, value: unknown) => {
    capturePosthogEvent(POSTHOG_EVENTS.agencyMergeFieldValueTyped, {
      agency_ids: agencyIds,
      field,
      matches_agency_value: activeAgencies.some((a) => areMergeValuesEqual(getMergeFieldValue(a, field), value)),
    });
  };

  const handleChoice = (field: MergeableAgencyField, choice: string) => {
    if (choice === CUSTOM_CHIP) {
      setCustom(field, true);
      return;
    }
    const groups = groupsByField[field];
    const groupIndex = Number(choice);
    const group = groups[groupIndex];
    if (!group) return;
    const wasCustom = customFields.has(field);
    if (!wasCustom && findMergeValueGroup(groups, values[field]) === groupIndex) return;
    typedFieldsRef.current.delete(field);
    touchedFieldsRef.current.add(field);
    setCustom(field, false);
    setFieldValue(field, group.value);
    capturePosthogEvent(POSTHOG_EVENTS.agencyMergeFieldValuePicked, {
      agency_ids: agencyIds,
      field,
      picked_agency_id: activeAgencies[group.members[0]].id,
      distinct_values: groups.length,
      holder_count: group.members.length,
    });
  };

  // Text and number controls capture on blur; switches and selects capture on change.
  const handleTyped = (field: MergeableAgencyField, value: unknown, captureNow: boolean) => {
    touchedFieldsRef.current.add(field);
    setFieldValue(field, value);
    if (captureNow) captureTyped(field, value);
    else typedFieldsRef.current.add(field);
  };

  const handleBlur = (field: MergeableAgencyField) => {
    if (!typedFieldsRef.current.delete(field)) return;
    captureTyped(field, values[field]);
  };

  const handleRemove = (agency: Agency, index: number) => {
    const remaining = activeAgencies.filter((a) => a.id !== agency.id);
    const next = reconcileMergeValues(values, remaining, customFields, touchedFieldsRef.current);
    // Fields this removal leaves invalid open in Custom and are named in the announcement.
    const newlyInvalid = MERGEABLE_AGENCY_FIELDS.filter(
      (field) => errors[field] === undefined && getMergeFieldError(field, next[field]) !== null,
    );
    const toCustom = newlyInvalid.filter((field) => MERGE_FIELD_INPUT[field].kind !== 'pick-only');
    pendingFocusRef.current = { index };
    setRemovedIds((prev) => [...prev, agency.id]);
    setValues(next);
    if (toCustom.length > 0) setCustomFields((prev) => new Set([...prev, ...toCustom]));
    const invalidNote =
      newlyInvalid.length > 0
        ? ` ${newlyInvalid.map((field) => MERGEABLE_AGENCY_FIELD_LABELS[field]).join(', ')} ${
            newlyInvalid.length === 1 ? 'needs' : 'need'
          } a value.`
        : '';
    setAnnouncement(
      `Removed ${agency.agencyName}. ${plural(remaining.length, 'agency', 'agencies')} left.${invalidNote}`,
    );
    capturePosthogEvent(POSTHOG_EVENTS.agencyMergeAgencyRemoved, {
      agency_ids: remaining.map((a) => a.id),
      removed_agency_id: agency.id,
      agency_count: remaining.length,
    });
  };

  const handleConfirm = () => {
    if (tooFew || auditLoading || hasErrors || hasBlockers || isPending) return;
    onSubmit({ agencyIds, values: toMergeAgencyValues(values) });
  };

  const disabledReason = tooFew
    ? 'Select at least two agencies to merge.'
    : auditLoading
      ? 'Wait for the merge audit to load.'
      : hasBlockers
        ? 'Resolve the sub-region overlap before merging.'
        : hasErrors
          ? 'Fix the fields marked with an error before merging.'
          : undefined;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isPending && onClose()}>
      <DialogContent
        title={`Merge ${plural(activeAgencies.length, 'agency', 'agencies')}`}
        preventDismiss
        closeButtonProps={{ disabled: isPending, 'data-testid': 'merge-agencies-close' }}
        className="max-w-4xl"
        data-testid="merge-agencies-dialog"
      >
        <DialogBody className="flex flex-col gap-5">
          <p className="text-sm text-grey-70">
            One new parent agency is created. Every selected agency becomes a child of it, keeps its own geometry, and
            redirects to the parent.
          </p>

          <section aria-labelledby={stripHeadingId} className="flex flex-col gap-2">
            <h3
              id={stripHeadingId}
              ref={stripHeadingRef}
              tabIndex={-1}
              className="text-xs font-semibold uppercase tracking-wide text-grey-70"
            >
              Selected agencies, in selection order
            </h3>
            <ul className="flex flex-wrap gap-2">
              {activeAgencies.map((agency, index) => (
                <li
                  key={agency.id}
                  data-testid={`merge-agencies-agency-${agency.agencyId}`}
                  className="inline-flex max-w-full items-center gap-2 rounded-full border border-grey-30 bg-grey-10 py-0.5 pr-1 pl-3 text-sm"
                >
                  <span className="font-medium text-primary-dark break-words">
                    {index + 1}. {agency.agencyName}
                  </span>
                  <span className="font-noto-mono text-xs text-grey-70">{agency.sourceTable}</span>
                  {agency.isMergedRecord && (
                    <Badge kind="outline" color="blue" size="small">
                      merged record
                    </Badge>
                  )}
                  <IconButton
                    ref={(el) => {
                      if (el) removeButtonsRef.current.set(agency.id, el);
                      else removeButtonsRef.current.delete(agency.id);
                    }}
                    icon="cross2"
                    variant="hollow"
                    className="size-6 rounded-full"
                    aria-label={`Remove ${index + 1}. ${agency.agencyName} from this merge`}
                    disabled={isPending || activeAgencies.length <= 1}
                    onClick={() => handleRemove(agency, index)}
                    data-testid={`merge-agencies-remove-${agency.agencyId}`}
                  />
                </li>
              ))}
            </ul>
            {/* Mounted empty so a removal is announced. */}
            <output aria-live="polite" className="sr-only">
              {announcement}
            </output>
          </section>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-md border border-grey-30 px-4 py-3 text-sm text-primary-dark">
            <span data-testid="merge-agencies-differing-count">
              <strong className="tabular-nums">{summary.differingFields.length}</strong> of {summary.fieldCount} fields
              differ
            </span>
            <span data-testid="merge-agencies-empty-count">
              <strong className="tabular-nums">{summary.emptyFields.length}</strong> empty on every agency
            </span>
            <span className="ml-auto flex items-center gap-2">
              <Switch
                id={showAgreedId}
                checked={showAgreed}
                onCheckedChange={(next) => {
                  setShowAgreed(next);
                  capturePosthogEvent(POSTHOG_EVENTS.agencyMergeDuplicatesToggled, {
                    agency_ids: agencyIds,
                    hide_duplicates: !next,
                  });
                }}
                data-testid="merge-agencies-hide-duplicates"
              />
              <Label htmlFor={showAgreedId} className="cursor-pointer text-sm text-grey-70">
                Show fields that agree
              </Label>
            </span>
          </div>

          <ul className="flex flex-col border-t border-grey-30" aria-label="Fields">
            {visibleFields.length === 0 && (
              <li data-testid="merge-agencies-all-agree" className="py-3 text-sm text-grey-70">
                Every field agrees. Turn on Show fields that agree to review the values.
              </li>
            )}
            {visibleFields.map(({ field, groups, expanded, isCustom }) =>
              expanded ? (
                <DifferingFieldRow
                  key={field}
                  field={field}
                  agencies={activeAgencies}
                  groups={groups}
                  value={values[field]}
                  isCustom={isCustom}
                  error={errors[field]}
                  onChoice={(choice) => handleChoice(field, choice)}
                  onTyped={(value, captureNow) => handleTyped(field, value, captureNow)}
                  onBlur={() => handleBlur(field)}
                />
              ) : (
                <AgreedFieldRow key={field} field={field} agencies={activeAgencies} value={values[field]} />
              ),
            )}
          </ul>

          {!tooFew && (
            <MergeAuditPanel
              agencies={activeAgencies}
              previews={previews}
              isLoading={auditQuery.isLoading}
              isError={auditQuery.isError}
              onExpand={handleAuditExpanded}
            />
          )}
        </DialogBody>
        <DialogFooter className="flex-wrap">
          <p data-testid="merge-agencies-status" className="mr-auto text-sm text-grey-70 tabular-nums">
            {plural(summary.differingFields.length, 'decision', 'decisions')},{' '}
            {plural(emptyValueCount, 'field', 'fields')} will be empty
          </p>
          <Button variant="secondary" onClick={onClose} disabled={isPending} data-testid="merge-agencies-cancel">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={tooFew || auditLoading || hasErrors || hasBlockers || isPending}
            tooltip={isPending ? undefined : disabledReason}
            data-testid="merge-agencies-confirm"
          >
            {isPending ? 'Merging…' : 'Merge into new parent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldName({ id, field, muted = false }: { id?: string; field: MergeableAgencyField; muted?: boolean }) {
  return (
    <div className={muted ? 'text-sm text-grey-70' : 'pt-1 text-sm font-medium text-primary-dark'}>
      <span id={id}>{MERGEABLE_AGENCY_FIELD_LABELS[field]}</span>
      <span className="block font-noto-mono text-xs font-normal text-grey-70">{field}</span>
    </div>
  );
}

const ROW_CLASS = 'grid grid-cols-1 gap-x-4 gap-y-2 border-b border-grey-30 py-3 sm:grid-cols-[12rem_minmax(0,1fr)]';

/** A field where the agencies hold at most one distinct non-empty value: shown, muted, with no choice. */
function AgreedFieldRow({
  field,
  agencies,
  value,
}: {
  field: MergeableAgencyField;
  agencies: Agency[];
  value: unknown;
}) {
  return (
    <li data-testid={`merge-agencies-field-${field}`} className={ROW_CLASS}>
      <FieldName field={field} muted />
      <div className="text-sm text-grey-70 wrap-anywhere">
        {isEmptyMergeValue(value) ? (
          <em>empty on every agency</em>
        ) : (
          <span className="font-noto-mono">{formatMergeValue(field, value, agencies)}</span>
        )}
      </div>
    </li>
  );
}

/** A field with a choice to make: one chip per distinct value, Custom, and who holds each value. */
function DifferingFieldRow({
  field,
  agencies,
  groups,
  value,
  isCustom,
  error,
  onChoice,
  onTyped,
  onBlur,
}: {
  field: MergeableAgencyField;
  agencies: Agency[];
  groups: MergeValueGroup[];
  value: unknown;
  isCustom: boolean;
  error: string | undefined;
  onChoice: (choice: string) => void;
  onTyped: (value: unknown, captureNow: boolean) => void;
  onBlur: () => void;
}) {
  const id = useId();
  const nameId = `${id}-name`;
  const errorId = `${id}-error`;
  const label = MERGEABLE_AGENCY_FIELD_LABELS[field];
  const total = agencies.length;
  const allowsCustom = isCustom || customAllowed(field, groups);
  const selectedIndex = findMergeValueGroup(groups, value);
  const selected = isCustom ? CUSTOM_CHIP : selectedIndex === -1 ? '' : String(selectedIndex);

  return (
    <li data-testid={`merge-agencies-field-${field}`} className={ROW_CLASS}>
      <FieldName id={nameId} field={field} />
      <div className="flex min-w-0 flex-col gap-2">
        <ValueChipGroup
          value={selected}
          onValueChange={onChoice}
          aria-labelledby={nameId}
          aria-describedby={error ? errorId : undefined}
        >
          {groups.map((group, index) => {
            const text = groupText(field, group, agencies);
            const chosen = selected === String(index);
            return (
              <ValueChip
                key={group.members[0]}
                value={String(index)}
                checked={chosen}
                label={text}
                empty={group.empty}
                count={`${group.members.length}/${total}`}
                aria-label={`${label}: ${text}, ${group.members.length} of ${total} agencies`}
                data-testid={`merge-agencies-chip-${field}-${index}`}
              />
            );
          })}
          {allowsCustom && (
            <ValueChip
              value={CUSTOM_CHIP}
              checked={isCustom}
              custom
              label="Custom…"
              aria-label={`${label}: custom value`}
              data-testid={`merge-agencies-custom-${field}`}
            />
          )}
        </ValueChipGroup>
        <p data-testid={`merge-agencies-holders-${field}`} className="flex flex-wrap gap-x-1.5 text-xs text-grey-70">
          <span>Which agency holds it:</span>
          {groups.map((group, index) => (
            <span key={group.members[0]} className="inline-flex max-w-full">
              {index > 0 && (
                <span aria-hidden="true" className="mr-1.5">
                  ·
                </span>
              )}
              <span className="max-w-64 truncate font-medium text-primary-dark">
                {groupText(field, group, agencies)}
              </span>
              : {group.members.map((member) => member + 1).join(', ')}
            </span>
          ))}
        </p>
        {isCustom && (
          <CustomValueControl
            field={field}
            label={label}
            value={value}
            errorId={error ? errorId : undefined}
            onTyped={onTyped}
            onBlur={onBlur}
          />
        )}
        {/* Mounted empty so a new error is announced. */}
        <output
          id={errorId}
          aria-live="polite"
          className="text-xs text-destructive"
          data-testid={`merge-agencies-value-${field}-error`}
        >
          {error ?? ''}
        </output>
      </div>
    </li>
  );
}

/**
 * Whether a field offers Custom: never for pick-only fields, and not for a boolean whose chips
 * already hold both Yes and No.
 */
function customAllowed(field: MergeableAgencyField, groups: readonly MergeValueGroup[]): boolean {
  const { kind } = MERGE_FIELD_INPUT[field];
  if (kind === 'pick-only') return false;
  if (kind !== 'boolean') return true;
  return !(groups.some((g) => g.value === true) && groups.some((g) => g.value === false));
}

/** The control for a field's Custom value, shown while its Custom chip is chosen; driven by `MERGE_FIELD_INPUT`. */
function CustomValueControl({
  field,
  label,
  value,
  errorId,
  onTyped,
  onBlur,
}: {
  field: MergeableAgencyField;
  label: string;
  value: unknown;
  /** Set while the field has an error. */
  errorId: string | undefined;
  onTyped: (value: unknown, captureNow: boolean) => void;
  onBlur: () => void;
}) {
  const id = useId();
  const testId = `merge-agencies-value-${field}`;
  const input = MERGE_FIELD_INPUT[field];
  const invalidProps = errorId ? { 'aria-invalid': true, 'aria-describedby': errorId } : {};
  const choiceProps = {
    id,
    'data-testid': testId,
    'aria-invalid': errorId ? true : undefined,
    'aria-describedby': errorId,
  };

  let control: ReactNode = null;
  switch (input.kind) {
    case 'text': {
      const TextControl = input.multiline ? TextArea : Input;
      control = (
        <TextControl
          id={id}
          value={typeof value === 'string' ? value : ''}
          onChange={(e: { target: { value: string } }) => onTyped(textToMergeValue(field, e.target.value), false)}
          onBlur={onBlur}
          data-testid={testId}
          {...invalidProps}
        />
      );
      break;
    }
    case 'url':
    case 'email':
      control = (
        <Input
          id={id}
          type={input.kind}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onTyped(textToMergeValue(field, e.target.value), false)}
          onBlur={onBlur}
          data-testid={testId}
          {...invalidProps}
        />
      );
      break;
    case 'number':
      control = (
        <Input
          id={id}
          type="number"
          min={0}
          step={input.integer ? 1 : 'any'}
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(e) => {
            const next = e.target.value === '' ? null : Number(e.target.value);
            onTyped(next === null || Number.isNaN(next) ? null : next, false);
          }}
          onBlur={onBlur}
          data-testid={testId}
          {...invalidProps}
        />
      );
      break;
    case 'boolean':
      control = (
        <Select
          {...choiceProps}
          options={BOOLEAN_OPTIONS}
          value={typeof value === 'boolean' ? String(value) : undefined}
          onValueChange={(next) => onTyped(next === null ? null : next === 'true', true)}
        />
      );
      break;
    case 'select':
      control =
        input.options.length > MAX_SELECT_OPTIONS ? (
          <ComboBox
            {...choiceProps}
            options={[...input.options]}
            value={typeof value === 'string' ? value : undefined}
            onValueChange={(next) => onTyped(next === '' ? null : next, true)}
          />
        ) : (
          <Select
            {...choiceProps}
            options={[...input.options]}
            value={typeof value === 'string' ? value : undefined}
            onValueChange={(next) => onTyped(next, true)}
          />
        );
      break;
    case 'pick-only':
      break;
  }

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-xs text-grey-70">
        Custom value <span className="sr-only">for {label}</span>
      </Label>
      {control}
    </div>
  );
}
